import React from "react";
import { useDashboardLayout } from "../../../../../../../../shared/frontend/dashboard/useDashboardLayout";
import { useUiPreferences } from "../../../../app/prefs/useUiPreferences";
import {
  PERSON_OVERVIEW_DEFAULT_ORDER,
  PERSON_OVERVIEW_WIDGET_KEYS,
  resolvePersonOverviewWidgets,
  type PersonOverviewWidgetState
} from "./personOverviewWidgets";

/**
 * Anordnung der Übersicht je Benutzer über den gemeinsamen Layout-Lebenszyklus des Dashboards
 * (`useDashboardLayout`). Gespeichert in den UI-Einstellungen unter `personOverviewLayout` — damit
 * übernehmen auch die Benutzer-Vorlagen die Anordnung, ohne eigenen Endpunkt.
 */
export function usePersonOverviewLayout() {
  const { prefs, isReady, patchPrefs } = useUiPreferences();
  // Der Adapter liest immer den neuesten Stand: Nach dem Speichern lädt der Layout-Hook sofort neu,
  // bevor der Zustand der UI-Einstellungen neu gerendert ist.
  const storedRef = React.useRef(prefs.personOverviewLayout);
  const patchRef = React.useRef(patchPrefs);
  React.useEffect(() => {
    storedRef.current = prefs.personOverviewLayout;
    patchRef.current = patchPrefs;
  }, [patchPrefs, prefs.personOverviewLayout]);

  const adapter = React.useMemo(() => ({
    load: async () => {
      const stored = storedRef.current;
      return {
        widgets: resolvePersonOverviewWidgets(stored?.widgets),
        widgetOrder: stored?.widgetOrder && stored.widgetOrder.length > 0 ? stored.widgetOrder : PERSON_OVERVIEW_DEFAULT_ORDER
      };
    },
    save: async (layout: { widgets: PersonOverviewWidgetState[]; widgetOrder: string[] }) => {
      const next = {
        widgets: layout.widgets.map((widget) => ({ key: widget.key, visible: widget.visible, ...(widget.limit != null ? { limit: widget.limit } : {}) })),
        widgetOrder: layout.widgetOrder
      };
      storedRef.current = next;
      patchRef.current({ personOverviewLayout: next });
    }
    // Stabiler Adapter: eine neue Identität würde den Layout-Hook bei jeder Neuzeichnung neu laden lassen.
  }), []);

  return useDashboardLayout<PersonOverviewWidgetState>(PERSON_OVERVIEW_WIDGET_KEYS, adapter, isReady);
}
